// Display formatting rules for track titles.
//
// The stored `title` field in Mongo is never mutated — this is display-only.
// The suffix ("DJ SPY REMIX", "ORIGINAL MIX", "DJ SPY EDIT", "DJ SPY MASHUP")
// is derived exclusively from the track's `track_type` field, per the Stage 6
// spec. Legacy titles that already contain a suffix are left untouched so we
// never double-append.
//
// Rules:
//   1. `track_type = remix`    → append " DJ SPY REMIX"   (default when missing)
//        Additionally: if `artist` is set and not already embedded in the
//        stored title, prefix with `${artist} - ` (attributes the original
//        artist). This mirrors the original Stage 1 behavior for remixes.
//   2. `track_type = original` → append " ORIGINAL MIX"
//   3. `track_type = edit`     → append " DJ SPY EDIT"
//   4. `track_type = mashup`   → append " DJ SPY MASHUP"
//   5. If the raw title already ends with ANY known suffix, use the title
//      as-is (never duplicate).

export const TRACK_TYPE_SUFFIX = {
  remix: "DJ SPY REMIX",
  original: "ORIGINAL MIX",
  edit: "DJ SPY EDIT",
  mashup: "DJ SPY MASHUP",
  funky: "DJ SPY FUNKY",
};

const ALL_SUFFIXES = Object.values(TRACK_TYPE_SUFFIX);

// Card meta line — genre for classical types; regional Edit Category
// only for Edit / Funky.  The type suffix (DJ SPY EDIT / DJ SPY FUNKY)
// lives in the track title, NOT here, so the small red line stays clean:
//   `01 / HOPEE`, `02 / KHALEEJI`, etc.
// Falls back to the type suffix alone if a category has not been set
// yet, so admins can still see what the record represents.
export function trackCardMetaLabel(track) {
  const type = String(track?.track_type || "").toLowerCase();
  if (type === "edit" || type === "funky") {
    const cat = String(track?.edit_category || "").trim().toUpperCase();
    return cat || TRACK_TYPE_SUFFIX[type];
  }
  return String(track?.genre || "").toUpperCase();
}

function normalizeType(value) {
  const t = String(value || "").trim().toLowerCase();
  return TRACK_TYPE_SUFFIX[t] ? t : "remix";
}

/** Returns true if `title` already contains any known suffix (case- and
 *  whitespace-insensitive). This is broader than an `endsWith` check so
 *  legacy titles with irregular spacing (e.g. "... DJ SPY  REMIX") are
 *  still recognized and never receive a duplicate suffix. */
function titleHasKnownSuffix(title) {
  const normalized = title.toLowerCase().replace(/\s+/g, " ").trim();
  return ALL_SUFFIXES.some((s) => normalized.includes(s.toLowerCase()));
}

export function formatTrackTitle(track) {
  if (!track) return "";
  const title = String(track.title || "").trim();
  if (!title) return "";
  const type = normalizeType(track.track_type);
  const suffix = TRACK_TYPE_SUFFIX[type];

  // Remix keeps the "Artist - Title DJ SPY REMIX" convention when both
  // artist and title are set.  Edit and Funky follow the same
  // "{artist} - {title} <SUFFIX>" pattern per the FINAL Phase A rule
  // so the displayed title matches the stored fields verbatim.
  // Originals + Mashups don't prepend the artist — originals are DJ SPY's
  // own work; mashups embed sources inside the title.
  const includeArtistPrefix = type === "remix" || type === "edit" || type === "funky";
  const artist = String(track.artist || "").trim();
  const artistLower = artist.toLowerCase();
  const withArtist =
    includeArtistPrefix && artist && !title.toLowerCase().includes(artistLower)
      ? `${artist} - ${title}`
      : title;

  // Never duplicate suffixes. If the title already ends with any of the
  // four known suffixes (including a wrongly-typed legacy title) we keep
  // the raw string. Editors can rename via Admin if they want to switch.
  if (titleHasKnownSuffix(withArtist)) return withArtist;
  return `${withArtist} ${suffix}`;
}

// Safe download filename derived from display title.
export function trackDownloadName(track) {
  const display = formatTrackTitle(track) || "track";
  return display.replace(/[\\/:*?"<>|]+/g, "").slice(0, 120);
}
